// React
import React, { useContext, useEffect, useState } from "react";
import { useObserver } from "mobx-react-lite";

// Core
import { useRouter } from "Core/Base";
import { Loader } from "Core/Components/Loader";

// Custom
import { Error } from "Custom/Components/Error";
import { FooterMenuBlock } from "./FooterMenuBlock";
import { DashboardPageStyles, MainPageStyles } from "Custom/StylesAppSpecific/DashboardPagesStyling";
import { Stores, StoresContext } from "Custom/Stores";

// Models
import { ApiStatus } from "Custom/Models/Enums/ApiStatus";

// Material UI
import { Box } from "@material-ui/core";
import { useTheme } from "@material-ui/core/styles";
import useMediaQuery from "@material-ui/core/useMediaQuery";

// SVG Icons
import { ReactComponent as DashboardLogo } from "../Content/dashboard.svg";
import { ReactComponent as Home } from "../Content/home.svg";
import { ReactComponent as Document } from "../Content/document.svg";
import { ReactComponent as Touch } from "../Content/touch.svg";
import { ReactComponent as Cardpayment } from "../Content/cardpayment.svg";
import { ReactComponent as Tool } from "../Content/tool.svg";
import { ReactComponent as Admin } from "../Content/admin_Icon.svg";
import { DashboardViewModel } from "Custom/ViewModels/DashboardViewModel";
import { SignaturesSectionViewModel } from "Custom/ViewModels/DashboardSubViewModels/SignaturesSectionViewModel";

export const PageMenu: React.FC = () => {
    // #region Initialization
    const router = useRouter();
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up("md")) ? true : false;
    const store = useContext<Stores>(StoresContext);
    const portalThemeStore = store.domain.PortalThemeStore;

    const mainPageStyles = MainPageStyles();
    const dashboardPageStyles = DashboardPageStyles();

    // Use the shared viewModel - only for non-SuperAdmin routes
    const isSuperAdminRoute = router.match.path === "/superadmin";
    const propertyId = (router.match.params as any).propertyId;
    const [viewModel, setViewModel] = useState(() => (isSuperAdminRoute ? null : new DashboardViewModel(propertyId)));

    // For Super Admin: disable tabs if no propertyId is selected
    const shouldDisableTabs = isSuperAdminRoute && !propertyId;
    const [signatureViewModel, setSignatureViewModel] = useState(() => (isSuperAdminRoute ? null : new SignaturesSectionViewModel(propertyId)));

    useEffect(() => {
        const newPropertyId = (router.match.params as any).propertyId;

        if (isSuperAdminRoute || !newPropertyId) {
            if (viewModel !== null) {
                setViewModel(null);
            }
            return;
        }

        if (viewModel === null) {
            setViewModel(new DashboardViewModel(newPropertyId));
            return;
        }

        if (newPropertyId !== viewModel.propertyId) {
            viewModel.changePlot(newPropertyId);
            setViewModel(new DashboardViewModel(newPropertyId));
            setSignatureViewModel(new SignaturesSectionViewModel(newPropertyId));
        }
    }, [propertyId, isSuperAdminRoute]);
    // #endregion Initialization

    // #region Render Handlers
    const renderBusy = () => <Loader />;
    const renderError = () => <Error errorText="Failed to retrieve information." />;

    const renderSidebarMenu = () => {
        if (!isDesktop) return null;

        // Build menu items - for Super Admin route, disable navigation until propertyId is selected
        const menuItems = [
            {
                title: "Dashboard",
                path: viewModel?.navigateToDashboard || "#",
                icon: DashboardLogo,
                ordinal: 0,
                disabled: shouldDisableTabs,
            },
            {
                title: "My Home",
                path: viewModel?.navigateToPropertyPathname || "#",
                icon: Home,
                ordinal: 1,
                disabled: shouldDisableTabs,
            },
            {
                title: "Documents",
                path: viewModel?.navigateToDocumentsPathname || "#",
                icon: Document,
                ordinal: 4,
                disabled: shouldDisableTabs,
            },
            {
                title: "Signatures",
                path: viewModel?.navigateToSignaturesPathname,
                icon: Document,
                ordinal: 6,
                showIndicator: signatureViewModel?.hasOutstandingSignatures,
                disabled: shouldDisableTabs,
            },
        ];

        // Add Admin tab for Super Admins
        if (store.domain.AccountStore.hasSuperAdminPermission) {
            menuItems.unshift({ title: "Admin", path: "/superadmin", icon: Admin, ordinal: -1, disabled: false });
        }

        // Add conditional menu items (only if viewModel exists)
        if (viewModel?.canViewIsChoicesToPlot) {
            menuItems.push({
                title: "Choices",
                path: viewModel.navigateToChoicesPathname,
                icon: Touch,
                ordinal: 2,
                disabled: shouldDisableTabs,
            });
        }

        if (viewModel?.canViewSnags && viewModel?.canViewReportSnags) {
            menuItems.push({
                title: "Snags",
                path: viewModel.navigateToSnagsPathname,
                icon: Tool,
                ordinal: 3,
                disabled: shouldDisableTabs,
            });
        }

        if (viewModel?.canViewPayments) {
            menuItems.push({
                title: "Payments",
                path: viewModel.navigateToPaymentPathname || "#",
                icon: Cardpayment,
                ordinal: 5,
                disabled: shouldDisableTabs,
            });
        }

        const hoverColour = portalThemeStore.secondaryColour || portalThemeStore.primaryColour || undefined;

        return (
            <Box className={`${dashboardPageStyles.sidebar} baseSidebar`}>
                <Box className={mainPageStyles.sidebarMenu}>
                    {menuItems
                        .sort((a, b) => a.ordinal - b.ordinal)
                        .map((item, index) => (
                            <Box key={index} className={mainPageStyles.sidebarMenuItem}>
                                <FooterMenuBlock
                                    title={item.title}
                                    icon={item.icon}
                                    path={item.path || ""}
                                    height="40px"
                                    width="40px"
                                    disabled={item.disabled}
                                    showIndicator={Boolean(item.showIndicator)}
                                    hoverColour={hoverColour}
                                />
                            </Box>
                        ))}
                </Box>
            </Box>
        );
    };

    const renderFooterMenu = () => {
        if (isDesktop) return null;

        const hoverColour = portalThemeStore.secondaryColour || portalThemeStore.primaryColour || undefined;

        return (
            <Box className={mainPageStyles.stickyFooterMenu}>
                {store.domain.AccountStore.hasSuperAdminPermission && (
                    <FooterMenuBlock title={"Admin"} path={"/superadmin"} icon={Admin} height="24px" width="25px" disabled={false} hoverColour={hoverColour} />
                )}
                <FooterMenuBlock
                    title={"Dashboard"}
                    path={viewModel?.navigateToDashboard || "#"}
                    icon={DashboardLogo}
                    height="24px"
                    width="25px"
                    disabled={shouldDisableTabs}
                    hoverColour={hoverColour}
                />
                <FooterMenuBlock
                    title={"My home"}
                    path={viewModel?.navigateToPropertyPathname || "#"}
                    icon={Home}
                    height="24px"
                    width="25px"
                    disabled={shouldDisableTabs}
                    hoverColour={hoverColour}
                />
                {viewModel?.canViewIsChoicesToPlot && (
                    <FooterMenuBlock
                        title={"Choices"}
                        path={viewModel.navigateToChoicesPathname}
                        icon={Touch}
                        height="24px"
                        width="25px"
                        disabled={shouldDisableTabs}
                        hoverColour={hoverColour}
                    />
                )}
                {viewModel?.canViewSnags && viewModel?.canViewReportSnags && (
                    <FooterMenuBlock
                        title={"Snags"}
                        path={viewModel.navigateToSnagsPathname}
                        icon={Tool}
                        height="24px"
                        width="25px"
                        disabled={shouldDisableTabs}
                        hoverColour={hoverColour}
                    />
                )}
                <FooterMenuBlock
                    title={"Documents"}
                    path={viewModel?.navigateToDocumentsPathname}
                    icon={Document}
                    height="24px"
                    width="25px"
                    disabled={shouldDisableTabs}
                    hoverColour={hoverColour}
                />
                {viewModel?.canViewPayments && (
                    <FooterMenuBlock
                        title={"Payments"}
                        path={viewModel.navigateToPaymentPathname}
                        icon={Cardpayment}
                        height="24px"
                        width="25px"
                        disabled={shouldDisableTabs}
                        hoverColour={hoverColour}
                    />
                )}
                <FooterMenuBlock
                    title={"Signatures"}
                    path={viewModel?.navigateToSignaturesPathname}
                    icon={Document}
                    height="24px"
                    width="25px"
                    showIndicator={signatureViewModel?.hasOutstandingSignatures ?? false}
                    disabled={shouldDisableTabs}
                    hoverColour={hoverColour}
                />
            </Box>
        );
    };

    const renderItems = () => (
        <Box>
            {renderSidebarMenu()}
            {renderFooterMenu()}
        </Box>
    );
    // #endregion Render Handlers

    // #region Main Component
    return useObserver(() => (
        <Box>
            <Box className={mainPageStyles.workspace}>
                {renderItems()}
                {viewModel && viewModel.ApiStatus === ApiStatus.error && renderError()}
            </Box>
        </Box>
    ));
    // #endregion Main Component
};
